import { Box, Checkbox, useTheme } from "@mui/material";
import { useLiveQuery } from "@tanstack/react-db";
import React, { useContext, useMemo, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import SelectGroupsOfFeeds from "@/components/SelectGroupsOfFeeds.tsx";
import SelectNonGroups from "@/components/SelectNonGroups.tsx";
import { FullInput } from "@/components/Utils.tsx";
import locator from "@/locator.ts";
import { feedsIHaveAccessToQuery, fetchWorkspaceCommandAliasesQuery, groupsQuery } from "@/models/repositories";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { lowerCaseToCapCase, matchesFilter } from "@/utils";

export default React.memo(function SelectRecipients({
  activeChannels,
  setActiveIds,
  toggleAllIds,
  allowNonAliasChannels,
}: {
  activeChannels: string[];
  setActiveIds: (value: string[]) => void;
  toggleAllIds: (value: string[]) => void;
  allowNonAliasChannels: boolean;
}) {
  const theme = useTheme();
  const { currentWorkspaceId: workspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const myCurrentWorkspaceMembershipId = myCurrentWorkspaceMembership?.id;

  /**
   * STATE
   */
  const [selectedCommandAliases, setSelectedCommandAliases] = useState<string[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [openGroups, setOpenGroups] = useState<string[]>([]);

  /**
   * Live Queries
   */
  const { data: groupRows } = useLiveQuery((q) =>
    workspaceId && myCurrentWorkspaceMembershipId ? groupsQuery(workspaceId, myCurrentWorkspaceMembershipId)(q) : null,
  );
  const groupList = groupRows ?? [];

  const { data: commandAliasRows } = useLiveQuery((q) =>
    workspaceId && myCurrentWorkspaceMembershipId
      ? fetchWorkspaceCommandAliasesQuery(workspaceId, myCurrentWorkspaceMembershipId)(q)
      : null,
  );
  const commandAliasList = commandAliasRows ?? [];

  const { data: feedListWithGroupRows } = useLiveQuery((q) =>
    workspaceId && myCurrentWorkspaceMembershipId
      ? feedsIHaveAccessToQuery({
          workspaceId,
          permissionName: "write",
          workspaceMembershipId: myCurrentWorkspaceMembershipId,
          allowNonAliasChannels,
          allowGroups: true,
        })(q)
      : null,
  );
  const feedListWithGroups = feedListWithGroupRows ?? [];

  const { data: feedListWithoutGroupRows } = useLiveQuery((q) =>
    workspaceId && myCurrentWorkspaceMembershipId
      ? feedsIHaveAccessToQuery({
          workspaceId,
          permissionName: "write",
          workspaceMembershipId: myCurrentWorkspaceMembershipId,
          allowNonAliasChannels,
          allowGroups: false,
        })(q)
      : null,
  );
  const feedListWithoutGroups = feedListWithoutGroupRows ?? [];

  /**
   * Transformers
   */
  const reducedAliases = useMemo(() => {
    return [...new Set(commandAliasList?.map((alias) => alias.alias))];
  }, [commandAliasList]);

  // Index aliases by feedId once. Rescanning the whole alias list per feed is O(feeds × aliases),
  // which on fleet-sized workspaces (thousands of each) blocks the main thread for seconds every
  // time these memos recompute (each keystroke in the filter, every reactive update during boot).
  const aliasesByFeedId = useMemo(() => {
    const map = new Map<string, typeof commandAliasList>();
    for (const alias of commandAliasList) {
      const list = map.get(alias.feedId);
      if (list) {
        list.push(alias);
      } else {
        map.set(alias.feedId, [alias]);
      }
    }
    return map;
  }, [commandAliasList]);

  const feedGroupsFiltered = useMemo(() => {
    return groupList
      .map((group) => {
        const feeds = feedListWithGroups
          ?.filter((g) => g.groupId === group.id)
          ?.filter((feed) => matchesFilter(feed.title, filter))
          ?.map((feed) => ({
            ...feed,
            commandAlias: aliasesByFeedId.get(feed.id as string) ?? [],
          }));
        return {
          ...group,
          feeds,
        };
      })
      .filter((group) => group?.feeds?.length > 0);
  }, [feedListWithGroups, groupList, filter, aliasesByFeedId]);

  const filteredNonGroups = useMemo(() => {
    return feedListWithoutGroups
      ?.filter((feed) => matchesFilter(feed.title, filter))
      ?.map((feed) => ({
        ...feed,
        commandAlias: aliasesByFeedId.get(feed.id as string) ?? [],
      }));
  }, [feedListWithoutGroups, filter, aliasesByFeedId]);

  /**
   * State Methods
   */
  const toggleSingleFeed = (id: string) => {
    if (activeChannels?.includes(id)) {
      setActiveIds(activeChannels?.filter((feedId) => feedId !== id));
    } else {
      setActiveIds([...activeChannels, id]);
    }
  };

  const toggleSingleAlias = (alias: string) => {
    const flatMapGroups = feedGroupsFiltered?.map((group) => group.feeds.flat()).flat();
    const combinedList = [...flatMapGroups, ...filteredNonGroups];
    if (selectedCommandAliases?.includes(alias)) {
      setSelectedCommandAliases(selectedCommandAliases?.filter((a) => a !== alias));

      const removeActiveChannelsThatHaveActiveAlias = combinedList
        ?.filter((feed) => feed.commandAlias?.some((cmd) => cmd.alias === alias))
        ?.map((feed) => feed.id?.toString());

      const newActiveChannels = activeChannels?.filter((id) => !removeActiveChannelsThatHaveActiveAlias?.includes(id));
      setActiveIds(newActiveChannels);
    } else {
      const addedValues = combinedList
        ?.filter((feed) => feed.commandAlias?.some((cmd) => cmd.alias === alias))
        ?.map((feed) => feed.id?.toString())
        ?.concat(activeChannels);
      setSelectedCommandAliases([...selectedCommandAliases, alias]);
      const uniqueIds = [...new Set(addedValues)];
      setActiveIds(uniqueIds);
    }
  };

  const toggleOpenGroup = (groupId: string) => {
    setOpenGroups((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
  };
  const toggleAllGroupFeeds = (ids: string[]) => toggleAllIds(ids);

  /**
   * Container Styles
   */
  const containerClass = {
    active: {
      background: theme.palette.secondary.main,
      borderRadius: "0.5rem",
      border: `solid 1px ${theme.palette.secondary.light}`,
    },
    inactive: {
      border: "solid 1px transparent",
    },
  };

  return (
    <Box>
      <Box sx={{ mb: 1 }} data-sb={TestIds.fleet.recipientFilter}>
        <FullInput
          aria-label={locator.feed.input.fleetMessage.modal.filterRecipients}
          placeholder={locator.feed.input.fleetMessage.modal.filterRecipients}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </Box>

      <Box
        sx={{
          maxHeight: "320px",
          width: "100%",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <SelectGroupsOfFeeds
              data={feedGroupsFiltered}
              index={0}
              openGroups={openGroups}
              activeChannels={activeChannels}
              toggleOpenGroup={toggleOpenGroup}
              toggleAllGroupFeeds={toggleAllGroupFeeds}
              toggleSingleFeed={toggleSingleFeed}
              containerClassList={containerClass}
            />

            <SelectNonGroups
              data={filteredNonGroups}
              index={1}
              activeChannels={activeChannels}
              toggleAllGroupFeeds={toggleAllGroupFeeds}
              toggleSingleFeed={toggleSingleFeed}
              openGroups={openGroups}
              toggleOpenGroup={toggleOpenGroup}
              containerClassList={containerClass}
            />
          </Box>
          <Box>
            <Box sx={{ minWidth: "160px" }}>
              {reducedAliases.map((commandAlias) => (
                <Box
                  key={`label-${commandAlias}`}
                  data-sb={TestIds.fleet.recipientAlias}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSingleAlias(commandAlias)}
                >
                  <Checkbox
                    sx={{ p: 0 }}
                    checked={selectedCommandAliases?.includes(commandAlias)}
                    aria-label="Select all"
                  />
                  <Box sx={{ grow: 1 }}>All {lowerCaseToCapCase(commandAlias)}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});
